import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Logo } from '../components/Logo';
import { LegalFooter } from '../components/LegalFooter';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Mail, ArrowLeft } from 'lucide-react';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Check for error in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    
    if (errorParam === 'invalid_token') {
      setError('Your login link has expired or is invalid. Please request a new one.');
    } else if (errorParam === 'user_not_found') {
      setError('User not found. Please try again.');
    } else if (errorParam === 'invalid_or_expired') {
      setError('Your login link has expired or is invalid. Please request a new one.');
    }
  }, []);

  // Update page title
  useEffect(() => {
    document.title = 'Login | PATH by Secret Sauce';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/magic-link', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="p-4 flex justify-between items-center">
          <a href="/">
            <Logo variant="full" size="default" />
          </a>
          <ThemeToggle />
        </header>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full elev-2">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-ss-teal/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-ss-teal" />
              </div>
              <h2 className="h3 mb-4">Check your email</h2>
              <p className="body mb-4">
                We've sent a magic link to <strong className="text-foreground">{email}</strong>
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Click the link in your email to sign in. The link will expire in 15 minutes.
              </p>
              <Button
                variant="ghost"
                onClick={() => { setSent(false); setEmail(''); }}
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to login
              </Button>
            </div>
          </div>
        </div>
        <LegalFooter minimal className="py-4" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="p-4 flex justify-between items-center">
        <a href="/">
          <Logo variant="full" size="default" />
        </a>
        <ThemeToggle />
      </header>
      
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full elev-2">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-accent" />
            </div>
            <h1 className="h3 mb-2">Welcome to The PATH Programme</h1>
            <p className="body text-muted-foreground">Enter your email to receive a magic link</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="block text-sm font-medium mb-2">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your.email@example.com"
                data-testid="email-input"
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              size="lg"
              data-testid="send-magic-link"
            >
              {loading ? 'Sending...' : 'Send Magic Link'}
            </Button>
          </form>

          <div className="mt-6 text-center flex items-center justify-center gap-4 text-sm">
            <a href="/" className="text-muted-foreground hover:text-foreground transition-smooth flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" />
              Back to home
            </a>
            <span className="text-muted-foreground">•</span>
            <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-smooth">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
      
      <LegalFooter minimal className="py-4" />
    </div>
  );
}

export default LoginPage;
